import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subMonths, subWeeks } from 'date-fns';
import { pl } from 'date-fns/locale';

const TopPerformers = ({ data, users, selectedMonth, type = 'employees', employeeId = null }) => {
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'quarter', 'year'
  const [performers, setPerformers] = useState([]);
  
  // Przygotowanie danych o najlepszych pracownikach/klientach
  useEffect(() => {
    console.log(`[TopPerformers LOG - ${type}] Props received:`, { data, users, selectedMonth });
    if (!data) return;
    
    // Określenie zakresu dat
    let startDate, endDate;
    const now = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        startDate = startOfMonth(selectedMonth);
        endDate = endOfMonth(selectedMonth);
        break;
      case 'quarter':
        startDate = startOfMonth(subMonths(now, 3));
        endDate = endOfMonth(now);
        break;
      case 'year':
        startDate = startOfMonth(subMonths(now, 12));
        endDate = endOfMonth(now);
        break;
      default:
        startDate = startOfMonth(selectedMonth);
        endDate = endOfMonth(selectedMonth);
    }
    
    if (type === 'employees') {
      calculateTopEmployees(data, users, startDate, endDate);
    } else {
      calculateTopClients(data, users, startDate, endDate, employeeId);
    }
  }, [data, selectedMonth, timeRange, type, employeeId]);
  
  // Obliczanie najlepszych pracowników
  const calculateTopEmployees = (data, users, startDate, endDate) => {
    const { sales } = data;
    
    if (!sales || !users || sales.length === 0 || users.length === 0) {
      setPerformers([]);
      return;
    }
    
    // Filtrowanie sprzedaży w wybranym zakresie dat
    const filteredSales = sales.filter(sale => {
      const saleDate = sale.completedAt?.toDate ? sale.completedAt.toDate() : new Date(sale.completedAt);
      return saleDate >= startDate && saleDate <= endDate;
    });
    
    // Grupowanie sprzedaży według pracowników
    const employeeSales = filteredSales.reduce((acc, sale) => {
      const employeeId = sale.employeeId;
      if (!employeeId) return acc;
      
      if (!acc[employeeId]) {
        acc[employeeId] = {
          id: employeeId,
          totalAmount: 0,
          salesCount: 0
        };
      }
      
      acc[employeeId].totalAmount += parseFloat(sale.amount) || 0;
      acc[employeeId].salesCount += 1;
      return acc;
    }, {});
    
    // Dodawanie danych pracowników
    Object.keys(employeeSales).forEach(employeeId => {
      const user = users.find(usr => usr.id === employeeId);
      if (user) {
        employeeSales[employeeId].name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        employeeSales[employeeId].email = user.email;
        employeeSales[employeeId].avatar = user.photoURL;
        employeeSales[employeeId].location = user.location;
      }
    });
    
    // Sortowanie pracowników według sprzedaży
    const sortedEmployees = Object.values(employeeSales)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 5); // Top 5
    
    console.log(`[TopPerformers LOG - ${type}] Sorted Employees:`, sortedEmployees);
    setPerformers(sortedEmployees);
  };
  
  // Obliczanie najlepszych klientów
  const calculateTopClients = (data, clients, startDate, endDate, employeeId) => {
    const { sales } = data;
    
    if (!sales || !clients || sales.length === 0 || clients.length === 0) {
      setPerformers([]);
      return;
    }
    
    // Filtrowanie sprzedaży w wybranym zakresie dat
    let filteredSales = sales.filter(sale => {
      const saleDate = sale.completedAt?.toDate ? sale.completedAt.toDate() : new Date(sale.completedAt);
      return saleDate >= startDate && saleDate <= endDate;
    });
    
    // Filtrowanie sprzedaży dla konkretnego pracownika, jeśli podano ID
    if (employeeId) {
      filteredSales = filteredSales.filter(sale => sale.employeeId === employeeId);
    }
    
    // Grupowanie sprzedaży według klientów
    const clientSales = filteredSales.reduce((acc, sale) => {
      const clientId = sale.clientId;
      if (!clientId) return acc;
      
      if (!acc[clientId]) {
        acc[clientId] = {
          id: clientId,
          totalAmount: 0,
          purchasesCount: 0
        };
      }
      
      acc[clientId].totalAmount += parseFloat(sale.amount) || 0;
      acc[clientId].purchasesCount += 1;
      return acc;
    }, {});
    
    // Dodawanie danych klientów
    Object.keys(clientSales).forEach(clientId => {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        clientSales[clientId].name = `${client.firstName || ''} ${client.lastName || ''}`.trim();
        clientSales[clientId].email = client.email;
        clientSales[clientId].phone = client.phone;
        clientSales[clientId].assignedTo = client.assignedTo;
      }
    });
    
    // Sortowanie klientów według zakupów
    const sortedClients = Object.values(clientSales)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 5); // Top 5
    
    console.log(`[TopPerformers LOG - ${type}] Sorted Clients:`, sortedClients);
    setPerformers(sortedClients);
  };
  
  // Obsługa zmiany zakresu czasu
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  // Formatowanie wartości
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2
    }).format(value);
  };
  
  // Renderowanie listy najlepszych pracowników/klientów
  const renderPerformersList = () => {
    if (performers.length === 0) {
      return (
        <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
          Brak danych do wyświetlenia
        </Typography>
      );
    }
    
    return (
      <List sx={{ width: '100%' }}>
        {performers.map((performer, index) => (
          <React.Fragment key={performer.id}>
            <ListItem alignItems="flex-start">
              <ListItemAvatar>
                <Avatar 
                  src={performer.avatar} 
                  alt={performer.name}
                  sx={{ 
                    bgcolor: index === 0 ? 'warning.main' : 
                            index === 1 ? 'primary.main' : 
                            index === 2 ? 'success.main' : 'grey.500',
                    border: index < 3 ? '2px solid' : 'none',
                    borderColor: index === 0 ? 'warning.main' : 
                                index === 1 ? 'primary.main' : 
                                index === 2 ? 'success.main' : 'transparent'
                  }}
                >
                  {index === 0 ? <StarIcon /> : <PersonIcon />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                      {performer.name || 'Nieznany'}
                    </Typography>
                    {index === 0 && (
                      <Chip 
                        size="small" 
                        color="warning" 
                        label="Top" 
                        icon={<StarIcon fontSize="small" />} 
                      />
                    )}
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      {type === 'employees' ? (
                        `Sprzedaż: ${formatCurrency(performer.totalAmount)} (${performer.salesCount} transakcji)`
                      ) : (
                        `Zakupy: ${formatCurrency(performer.totalAmount)} (${performer.purchasesCount} transakcji)`
                      )}
                    </Typography>
                    <br />
                    <Typography component="span" variant="body2">
                      {type === 'employees' ? (
                        `${performer.email || ''} ${performer.location ? `• ${performer.location}` : ''}`
                      ) : (
                        `${performer.email || ''} ${performer.phone ? `• ${performer.phone}` : ''}`
                      )}
                    </Typography>
                  </React.Fragment>
                }
              />
            </ListItem>
            {index < performers.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>
    );
  };
  
  return (
    <Card elevation={3}>
      <CardHeader
        title={type === 'employees' ? "Najlepsi pracownicy" : "Najlepsi klienci"}
        action={
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="time-range-select-label">Zakres</InputLabel>
            <Select
              labelId="time-range-select-label"
              id="time-range-select"
              value={timeRange}
              label="Zakres"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="week">Tydzień</MenuItem>
              <MenuItem value="month">Miesiąc</MenuItem>
              <MenuItem value="quarter">Kwartał</MenuItem>
              <MenuItem value="year">Rok</MenuItem>
            </Select>
          </FormControl>
        }
      />
      <CardContent sx={{ p: 0 }}>
        {renderPerformersList()}
      </CardContent>
    </Card>
  );
};

export default TopPerformers;
